import { Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { setSubLoading } from "../../Redux/Services/AuthSlice";
import { NotificationSound } from "../CommonComponents/CommonNotification";
import { ApiCall, deleteData, encryptPassword, sanitizeInput } from "../CommonComponents/CustomHooks";
import { initializeFilterData } from "../CommonComponents/EnvConfig";
import { handleDownloadExcel } from "../CommonComponents/ExportData";
import { FormButton, InputBox, Switch } from "../CommonComponents/InputBox";
import { Table } from "../CommonComponents/Table";

const CountryMaster = () => {
    const inputrefs = useRef([])
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [isEdit, setIsEdit] = useState(false)
    const [tableData, setTableData] = useState([])
    const [currentPageNo, setCurrentPageNo] = useState(1);
    const [filterData, setFilterData] = useState(initializeFilterData)
    const [sortConfig, setSortConfig] = useState({ SortColumn: null, SortOrder: null });

    const initialFormData = {
        CountryID: '',
        countryCode: '',
        countryName: '',
        isStateApplicable: false,
        isPinCodeApplicable: false,
        active: true,
    }

    const [formData, setFormData] = useState(initialFormData);
    const GetCountryMaster = async (CurrentPage) => {
        let PageIndex = 1
        if (CurrentPage !== undefined && typeof (CurrentPage) !== "object") {
            PageIndex = CurrentPage;
            setCurrentPageNo(CurrentPage);
        }
        else {
            setCurrentPageNo(1);
        }
        let data = {
            ...filterData,
            ...sortConfig,
            "PageIndex": PageIndex.toString(),
            "Type": "S",
        }
        setIsLoading(true)
        let res = await ApiCall(`/api/Master/ShowCountryMaster`, data)
        if (res.Status === "SUCCESS") {
            setTableData(res)
        }
        if (res.Status === "ERROR") {
            setTableData([])
        }
        setIsLoading(false)
        if (res.Status === 'EXPIRED' || res.Status === 'UNAUTHORIZED') {
            await NotificationSound(res.Status, res.Message, res?.focus, inputrefs);
        }
    }

    const handleClear = () => {
        setFormData({
            ...initialFormData,
        });
        filterData.SearchText = '';
        inputrefs.current['txtCountryCode'].focus()
        setIsEdit(false)
        GetCountryMaster();
    }

    const handleSubmit = async () => {
        await dispatch(setSubLoading(true))
        let data = await ApiCall(`/api/Master/InsertUpdateCountryMaster`, { ...formData, password: encryptPassword(formData.password) })
        await dispatch(setSubLoading(false))
        await NotificationSound(data.status, data.message, data?.focus, inputrefs, handleClear);
    };

    const handleEditData = async (id) => {
        await dispatch(setSubLoading(true))
        await setIsEdit(true);
        let editData = await ApiCall(`/api/Master/EditCountryMaster`, { CountryID: id })
        await dispatch(setSubLoading(false))
        if (editData.status === 'SUCCESS') {
            setFormData((pre) => ({
                ...pre, ...editData,
            }))
            await inputrefs.current[editData?.focus].focus();
        } else {
            await NotificationSound(editData.status, editData.message, editData?.focus, inputrefs);
        }
    }

    const handleDelete = async (id) => {
        await dispatch(setSubLoading(true))
        await deleteData(`/api/Master/DeleteCountryMaster`, { CountryID: id }, GetCountryMaster)
        await dispatch(setSubLoading(false))
    }

    const handleChange1 = (i, p) => {
        GetCountryMaster(p)
    };

    const handleSorting = (SortColumn) => {
        let SortOrder = 'ASC';
        if (sortConfig && sortConfig.SortColumn === SortColumn && sortConfig.SortOrder === 'ASC') {
            SortOrder = 'DESC';
        }
        setSortConfig({ SortColumn, SortOrder });
    };

    useEffect(() => {
        document.title = `Country Master`
        inputrefs.current['txtCountryCode'].focus()
    }, [])

    useEffect(() => {
        GetCountryMaster();
    }, [sortConfig]);

    return (
        <>
            <div className='bg-secondary-50 shadow-lg rounded-md p-3'>
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-primary-600" />
                        <h2 className="text-xl font-semibold text-primary-600">Country Master</h2>
                    </div>
                </div>
                <div className="card-body grid grid-cols-1 md:grid-cols-5 gap-3">
                    <InputBox
                        id="txtCountryCode"
                        label="Country Code"
                        placeholder="Enter Country Code"
                        disabled={isEdit}
                        maxLength='3'
                        required={true}
                        value={formData.countryCode}
                        onChange={(e) =>
                            setFormData({ ...formData, countryCode: sanitizeInput(e.target.value) })
                        }
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                inputrefs.current['txtCountryName'].focus()
                            }
                        }}
                    />
                    <InputBox
                        id="txtCountryName"
                        label="Country Name"
                        placeholder="Enter Country Name"
                        maxLength='50'
                        required={true}
                        value={formData.countryName}
                        onChange={(e) =>
                            setFormData({ ...formData, countryName: sanitizeInput(e.target.value) })
                        }
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                inputrefs.current['txtIsStateApplicable'].focus()
                            }
                        }}
                    />
                    <Switch
                        id="txtIsStateApplicable"
                        label="State Applicable"
                        divclassname='mt-2'
                        checked={formData.isStateApplicable}
                        onChange={(e) =>
                            setFormData({ ...formData, isStateApplicable: e.target.checked })
                        }
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                inputrefs.current['txtIsPinCodeApplicable'].focus()
                            }
                        }}
                    />
                    <Switch
                        id="txtIsPinCodeApplicable"
                        label="Pincode Applicable"
                        divclassname='mt-2'
                        checked={formData.isPinCodeApplicable}
                        onChange={(e) =>
                            setFormData({ ...formData, isPinCodeApplicable: e.target.checked })
                        }
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                inputrefs.current['txtActive'].focus()
                            }
                        }}
                    />
                    <Switch
                        id="txtActive"
                        label="Active"
                        checked={formData.active}
                        divclassname='mt-2'
                        onChange={(e) =>
                            setFormData({ ...formData, active: e.target.checked })
                        }
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                inputrefs.current['btnSub'].focus()
                            }
                        }}
                    />

                </div>

                <div className='card-body'>
                    <FormButton
                        id={'btnSub'}
                        inputrefs={inputrefs}
                        handleSubmit={handleSubmit}
                        handleExport={async () => {
                            await dispatch(setSubLoading(true))
                            handleDownloadExcel(`/api/Master/ShowCountryMaster`, filterData, 'Country Master')
                            await dispatch(setSubLoading(false))
                        }}
                        handleClear={handleClear}
                    />
                </div>
            </div>
            <div className="mt-3">
                <Table
                    handleEdit={handleEditData}
                    handleDelete={handleDelete}
                    loading={isLoading}
                    PageName={'CountryMaster'}
                    handleFilter={() => {
                        GetCountryMaster()
                        setFilterData({
                            ...filterData,
                        })
                    }}
                    filterData={filterData}
                    tableData={tableData}
                    setFilterData={setFilterData}
                    currentPageNo={currentPageNo}
                    handleChange1={handleChange1}
                    sortConfig={sortConfig}
                    handleSorting={handleSorting}
                />
            </div>
        </>
    )
}
export default CountryMaster;